import { useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";
import { WirelessDeviceSerialLookupLink, WirelessHubTabs, formatWirelessCell } from "./wirelessUiShared";
import { WirelessReportDataPanel } from "./WirelessReportDataPanel";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "checkins", label: "Checkins" },
  { key: "currents", label: "Hourly currents" },
  { key: "events", label: "Events" },
] as const;

type DeviceTab = (typeof TABS)[number]["key"];

function deviceSerialLabel(fields: Record<string, string | number | null> | undefined): string {
  if (fields == null) return "";
  const serial = String(fields.FCIUniqueSerialNumber ?? "").trim();
  if (serial !== "") return serial;
  const imei = String(fields.IMEINumber ?? "").trim();
  if (imei !== "") return imei;
  const name = String(fields.Real_Name ?? "").trim();
  return name;
}

export function WirelessDeviceDetailPage() {
  const { deviceId: deviceIdParam } = useParams();
  const deviceId = Number(deviceIdParam);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = useMemo((): DeviceTab => {
    const raw = searchParams.get("tab");
    if (raw === "checkins" || raw === "currents" || raw === "events" || raw === "overview") return raw;
    return "overview";
  }, [searchParams]);

  const detailQuery = useQuery({
    queryKey: ["wireless", "device", deviceId],
    queryFn: () =>
      apiFetch<{
        device: {
          deviceId: number;
          fields: Record<string, string | number | null>;
          organizationName: string | null;
          groupNames: string | null;
        };
      }>(`/api/wireless/devices/${deviceId}`),
    enabled: Number.isFinite(deviceId) && deviceId > 0,
  });

  const device = detailQuery.data?.device;
  const serialLabel = deviceSerialLabel(device?.fields);
  const title = serialLabel !== "" ? serialLabel : `Device ${deviceId}`;
  const downloadCsvRef = useRef<(() => Promise<void>) | null>(null);
  const [downloadDisabled, setDownloadDisabled] = useState(true);
  const reportTab = activeTab === "checkins" || activeTab === "currents" || activeTab === "events";

  function setTab(tab: string) {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next, { replace: true });
  }

  if (!Number.isFinite(deviceId) || deviceId <= 0) {
    return <p className="text-sm text-red-700">Invalid device.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp
          hoverHint="Wireless portal device with check-in and event history. Title is the device serial when known."
          breadcrumbLabel={title}
        >
          {title}
        </PageTitleWithHelp>
        {reportTab ? (
          <Button
            type="button"
            disabled={downloadDisabled}
            onClick={() => {
              void downloadCsvRef.current?.();
            }}
          >
            Download CSV
          </Button>
        ) : null}
      </div>

      <WirelessHubTabs ariaLabel="Device sections" tabs={[...TABS]} activeKey={activeTab} onChange={setTab} />

      {activeTab === "overview" ? (
        detailQuery.isLoading ? (
          <p className="text-sm text-slate-600">Loading device...</p>
        ) : detailQuery.isError || !device ? (
          <p className="text-sm text-red-700">Device not found.</p>
        ) : (
          <div className="space-y-4">
            <Card>
              <CardBody className="grid gap-2 py-4 text-sm sm:grid-cols-2">
                <p>
                  <span className="font-medium text-slate-700">Device serial:</span>{" "}
                  <WirelessDeviceSerialLookupLink
                    serial={String(device.fields.FCIUniqueSerialNumber ?? "")}
                  />
                </p>
                <p>
                  <span className="font-medium text-slate-700">IMEI:</span>{" "}
                  {formatWirelessCell(device.fields.IMEINumber) || "—"}
                </p>
                <p>
                  <span className="font-medium text-slate-700">Name:</span>{" "}
                  {formatWirelessCell(device.fields.Real_Name) || "—"}
                </p>
                <p>
                  <span className="font-medium text-slate-700">Organization:</span>{" "}
                  {device.organizationName ?? "—"}
                </p>
                <p>
                  <span className="font-medium text-slate-700">Groups:</span> {device.groupNames ?? "—"}
                </p>
                {device.fields.OrganizationID != null ? (
                  <p>
                    <span className="font-medium text-slate-700">Organization link:</span>{" "}
                    <Link
                      to={`/wireless/organizations/${device.fields.OrganizationID}`}
                      className="text-brand-700 hover:underline"
                    >
                      Open organization
                    </Link>
                  </p>
                ) : null}
              </CardBody>
            </Card>
            <Card>
              <CardBody className="p-0">
                <table className="w-full text-sm">
                  <tbody>
                    {Object.entries(device.fields).map(([key, value]) => (
                      <tr key={key} className="border-b border-slate-100">
                        <th className="w-56 px-3 py-2 text-left font-medium text-slate-600">{key}</th>
                        <td className="px-3 py-2">{formatWirelessCell(value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </div>
        )
      ) : null}

      {activeTab === "checkins" ? (
        <WirelessReportDataPanel
          kind="checkins"
          fixedDeviceId={deviceId}
          downloadCsvRef={downloadCsvRef}
          onDownloadDisabledChange={setDownloadDisabled}
        />
      ) : null}
      {activeTab === "currents" ? (
        <WirelessReportDataPanel
          kind="currents"
          fixedDeviceId={deviceId}
          downloadCsvRef={downloadCsvRef}
          onDownloadDisabledChange={setDownloadDisabled}
        />
      ) : null}
      {activeTab === "events" ? (
        <WirelessReportDataPanel
          kind="events"
          fixedDeviceId={deviceId}
          downloadCsvRef={downloadCsvRef}
          onDownloadDisabledChange={setDownloadDisabled}
        />
      ) : null}
    </div>
  );
}
